/* Blocks shared by the Camps & Trips index (src/pages/Trips.jsx) and
   every trip page (src/pages/trips/CampPage.jsx). Copy comes from
   src/data/camps.js (the catalogue of record). */

import React from "react";
import LocaleLink from "../../components/LocaleLink";
import { useL } from "../../i18n/LocaleContext";
import { CAMPS_COPY, CAMP_KINDS, DESTINATION_OPTIONS } from "../../data/camps";
import { CatalogCard, EnquiryForm, ThreeCards, CheckList } from "../../components/catalog/CatalogParts";

/* Card props for a resolved camp record. */
export function campCard(c, kinds, copy) {
  return {
    href: c.href,
    photo: c.photos[0],
    badge: c.status.label,
    tone: c.status.tone,
    eyebrow: `${kinds[c.kind]} · ${c.country}`,
    title: c.title,
    text: c.tagline,
    facts: c.cardFacts,
    more: c.kind === "open" ? copy.seeCamp : copy.seeTrip,
  };
}

/* The one camp at the top of the index, shown wide. RAW record. */
export function FeaturedCamp({ camp }) {
  const c = useL(camp);
  const kinds = useL(CAMP_KINDS);
  const copy = useL(CAMPS_COPY);
  return <CatalogCard {...campCard(c, kinds, copy)} featured eager />;
}

/* A grid of camp cards. `camps` are RAW records; resolved here.
   `anywhere` closes the grid with the "Somewhere else in mind?" tile,
   sized so the grid always ends square (see .cat-tile-cell). */
export function CampGrid({ camps, large = false, anywhere = false }) {
  const list = useL(camps);
  const kinds = useL(CAMP_KINDS);
  const copy = useL(CAMPS_COPY);
  return (
    <ul className={`cat-grid${large ? " cat-grid--two" : ""}`}>
      {list.map((c) => (
        <li key={c.slug}>
          <CatalogCard {...campCard(c, kinds, copy)} large={large} />
        </li>
      ))}
      {anywhere && (
        <li className="cat-tile-cell">
          <div className="cat-tile">
            <span className="cat-tile-n" aria-hidden="true" dir="ltr">{copy.anywhere.number}</span>
            <h3 className="cat-tile-title">{copy.anywhere.title}</h3>
            <p className="cat-tile-text">{copy.anywhere.body}</p>
            <a href="#enquire" className="btn btn-gold cat-tile-btn">{copy.enquiry.title}</a>
          </div>
        </li>
      )}
    </ul>
  );
}

/* The numbers from the last UK camp. */
export function ResultsBand({ results, eyebrow, title, link }) {
  return (
    <section className="cat-results" aria-label={title || eyebrow}>
      <div className="cat-results-inner">
        <div className="cat-results-head">
          <p className="cat-eyebrow">{eyebrow}</p>
          {title && <p className="cat-results-title">{title}</p>}
          {link}
        </div>
        {results.items.map((r, i) => (
          <div className="cat-result" key={i}>
            <span><span className="cat-result-v">{r.v}</span><span className="cat-result-of">{r.of}</span></span>
            <span className="cat-result-l">{r.l}</span>
          </div>
        ))}
        <p className="cat-results-source">{results.source}</p>
      </div>
    </section>
  );
}

export function MethodSection() {
  const copy = useL(CAMPS_COPY);
  return (
    <section className="cat-section cat-section--white" aria-labelledby="method-title">
      <div className="cat-wrap">
        <h2 id="method-title" className="cat-h2 cat-h2--gap">{copy.method.eyebrow}</h2>
        <ThreeCards items={copy.method.items} />
      </div>
    </section>
  );
}

export function SafetySection() {
  const copy = useL(CAMPS_COPY);
  const s = copy.safety;
  return (
    <section className="cat-section" aria-labelledby="safety-title">
      <div className="cat-wrap">
        <p className="cat-eyebrow">{s.eyebrow}</p>
        <h2 id="safety-title" className="cat-h2">{s.title}</h2>
        <div className="cat-two cat-two--spaced">
          <div className="cat-panel cat-panel--accent">
            <h3 className="cat-panel-title">{s.safetyTitle}</h3>
            <CheckList items={s.safetyItems} />
          </div>
          <div className="cat-panel cat-panel--accent">
            <h3 className="cat-panel-title">{s.commsTitle}</h3>
            <CheckList items={s.commsItems} />
          </div>
        </div>
      </div>
    </section>
  );
}

export function BookingSection() {
  const copy = useL(CAMPS_COPY);
  return (
    <section className="cat-section cat-section--white" aria-labelledby="book-title">
      <div className="cat-wrap">
        <h2 id="book-title" className="cat-h2 cat-h2--gap">{copy.book.eyebrow}</h2>
        <ThreeCards items={copy.book.steps} numbered />
      </div>
    </section>
  );
}

/* Contact details beside the enquiry form. */
export function EnquirySection({ defaultValue = "" }) {
  const copy = useL(CAMPS_COPY);
  const options = useL(DESTINATION_OPTIONS);
  const e = copy.enquiry;
  const c = copy.contact;
  return (
    <section className="cat-section cat-section--navy" id="enquire" aria-labelledby="enquire-title">
      <div className="cat-enquiry-inner">
        <div>
          <p className="cat-eyebrow">{e.eyebrow}</p>
          <h2 id="enquire-title" className="cat-h2">{e.title}</h2>
          <p className="cat-sub">{e.lead}</p>
          <ul className="cat-contact">
            <li>
              <span className="cat-contact-k">{c.emailLabel}</span>
              <a className="cat-contact-v" href={`mailto:${c.email}`}><bdi>{c.email}</bdi></a>
            </li>
            <li>
              <span className="cat-contact-k">WhatsApp</span>
              <a className="cat-contact-v" href={`https://wa.me/${c.whatsapp}`} target="_blank" rel="noopener noreferrer"><bdi dir="ltr">{c.whatsappDisplay}</bdi></a>
            </li>
            <li>
              <span className="cat-contact-k">Instagram</span>
              <a className="cat-contact-v" href={`https://www.instagram.com/${c.instagram}`} target="_blank" rel="noopener noreferrer"><bdi>@{c.instagram}</bdi></a>
            </li>
            <li>
              <span className="cat-contact-k">{c.visitLabel}</span>
              <span className="cat-contact-v">{c.office}</span>
            </li>
          </ul>
        </div>
        <div className="cat-form">
          <EnquiryForm copy={e} options={options} defaultValue={defaultValue} sourcePrefix="trips" event="trip_enquiry_submit" />
        </div>
      </div>
    </section>
  );
}

/* Small helper for in-text links to other site pages. */
export function TextLink({ to, children }) {
  return <LocaleLink to={to} className="cat-link">{children}</LocaleLink>;
}
